import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { APP_VERSION, BUILD_TIME } from '../config/buildInfo';

const Sidebar = ({ collapsed = false, onToggleSidebar }) => {
  const user = useSelector((state) => state.auth.user);
  const location = useLocation();
  const [isSystemAdminOpen, setIsSystemAdminOpen] = useState(false);
  const [isReportsOpen, setIsReportsOpen] = useState(location.pathname.startsWith('/reports'));
  const [activeFlyout, setActiveFlyout] = useState(null);

  const toggleSystemAdminMenu = () => {
    if (collapsed) {
      setActiveFlyout(null);
      return;
    }

    setIsSystemAdminOpen(!isSystemAdminOpen);
  };

  const toggleReportsMenu = () => {
    if (collapsed) {
      setActiveFlyout(null);
      return;
    }

    setIsReportsOpen(!isReportsOpen);
  };

  const getLinkClass = (path) =>
    `sidebar-link ${location.pathname === path ? 'active' : ''}`;

  const closeMobileMenu = () => {
    setActiveFlyout(null);
    if (window.innerWidth <= 900 && !collapsed) {
      onToggleSidebar?.();
    }
  };

  const showReportsMenu = collapsed ? activeFlyout === 'reports' : isReportsOpen;
  const showSystemAdminMenu = collapsed ? activeFlyout === 'system-admin' : isSystemAdminOpen;

  return (
    <aside className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-logo">
        <div className="sidebar-brand" title="Royal Medical Center">
          <span className="sidebar-brand-mark">
            <img
              src="rmc-mark.png"
              alt="RMC"
            />
          </span>
          <img
            className="sidebar-brand-full"
            src="logo.png"
            alt="Royal Medical Center"
          />
        </div>
      </div>

      <ul className="sidebar-menu">
        <li className="sidebar-section-label">Main</li>
        <li>
          <Link
            to="/dashboard"
            className={getLinkClass('/dashboard')}
            title="Dashboard"
            onClick={closeMobileMenu}
          >
            <i className="bi bi-speedometer2" aria-hidden="true"></i>
            <span className="sidebar-label">Dashboard</span>
          </Link>
        </li>
        <li>
          <Link
            to="/incident"
            className={getLinkClass('/incident')}
            title="Create Incident"
            onClick={closeMobileMenu}
          >
            <i className="bi bi-plus-square" aria-hidden="true"></i>
            <span className="sidebar-label">Create Incident</span>
          </Link>
        </li>
        {user && ['Admin', 'Quality Manager', 'Quality Staff'].includes(user.role) && (
          <>
          <li className="sidebar-section-label">Reports</li>
          <li
            className={`sidebar-menu-item has-submenu ${collapsed && activeFlyout === 'reports' ? 'flyout-open' : ''}`}
            onMouseEnter={() => collapsed && setActiveFlyout('reports')}
            onMouseLeave={() => collapsed && setActiveFlyout(null)}
            onFocus={() => collapsed && setActiveFlyout('reports')}
            onBlur={(event) => collapsed && !event.currentTarget.contains(event.relatedTarget) && setActiveFlyout(null)}
          >
            <button
              className={`sidebar-link sidebar-button ${isReportsOpen ? 'open' : ''}`}
              onClick={toggleReportsMenu}
              title="Reports"
            >
              <i className="bi bi-file-earmark-spreadsheet" aria-hidden="true"></i>
              <span className="sidebar-label">Reports</span>
              <i
                className={`sidebar-chevron ${isReportsOpen ? 'bi bi-chevron-up' : 'bi bi-chevron-down'}`}
              ></i>
            </button>

            {showReportsMenu && (
              <ul className={`sidebar-submenu ${collapsed ? 'sidebar-flyout' : ''}`}>
                {collapsed && <li className="sidebar-flyout-title">Reports</li>}
                <li>
                  <Link
                    to="/reports/ovr-registry"
                    className={getLinkClass('/reports/ovr-registry')}
                    onClick={closeMobileMenu}
                  >
                    <span className="sidebar-label">OVR Registry</span>
                  </Link>
                </li>
              </ul>
            )}
          </li>
          </>
        )}

        {user && user.role === 'Admin' && (
          <>
            <li className="sidebar-section-label">Administration</li>
            <li
              className={`sidebar-menu-item has-submenu ${collapsed && activeFlyout === 'system-admin' ? 'flyout-open' : ''}`}
              onMouseEnter={() => collapsed && setActiveFlyout('system-admin')}
              onMouseLeave={() => collapsed && setActiveFlyout(null)}
              onFocus={() => collapsed && setActiveFlyout('system-admin')}
              onBlur={(event) => collapsed && !event.currentTarget.contains(event.relatedTarget) && setActiveFlyout(null)}
            >
              <button
                className={`sidebar-link sidebar-button ${isSystemAdminOpen ? 'open' : ''}`}
                onClick={toggleSystemAdminMenu}
                title="System Admin"
              >
                <i className="bi bi-gear" aria-hidden="true"></i>
                <span className="sidebar-label">System Admin</span>
                <i
                  className={`sidebar-chevron ${isSystemAdminOpen ? 'bi bi-chevron-up' : 'bi bi-chevron-down'}`}
                ></i>
              </button>

              {showSystemAdminMenu && (
                <ul className={`sidebar-submenu ${collapsed ? 'sidebar-flyout' : ''}`}>
                  {collapsed && <li className="sidebar-flyout-title">System Admin</li>}
                  <li>
                    <Link
                      to="/register"
                      className={getLinkClass('/register')}
                      onClick={closeMobileMenu}
                    >
                      <span className="sidebar-label">Register User</span>
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/roles"
                      className={getLinkClass('/roles')}
                      onClick={closeMobileMenu}
                    >
                      <span className="sidebar-label">Create Role</span>
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/departments"
                      className={getLinkClass('/departments')}
                      onClick={closeMobileMenu}
                    >
                      <span className="sidebar-label">Create Department</span>
                    </Link>
                  </li>
                </ul>
              )}
            </li>
          </>
        )}
      </ul>
      <div className="sidebar-version" title={BUILD_TIME ? `Build: ${BUILD_TIME}` : `Version ${APP_VERSION}`}>
        <span>v{APP_VERSION}</span>
      </div>
    </aside>
  );
};

export default Sidebar;
